import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardCheck } from 'lucide-react';
import { toplamMetni } from '@/pages/insaat/ReportDetail';

const statusLabels = {
  submitted: 'Gönderildi',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
};

const statusColors = {
  submitted: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]',
};

export default function ReportList() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [sites, setSites] = useState([]);
  const [subs, setSubs] = useState([]);
  // Şefe giden "eksik raporlar" WhatsApp bildirimi buraya ?tarih=&site_id= ile açar
  const [arama] = useSearchParams();
  const [siteFilter, setSiteFilter] = useState(arama.get('site_id') || '');
  const [subFilter, setSubFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(arama.get('tarih') || '');
  const [dateTo, setDateTo] = useState(arama.get('tarih') || '');

  useEffect(() => {
    api.get('/insaat/sites').then((r) => setSites(r.data)).catch(() => {});
    api.get('/insaat/subcontractors').then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchReports(); }, [siteFilter, subFilter, statusFilter, dateFrom, dateTo]);

  const fetchReports = async () => {
    const params = {};
    if (siteFilter) params.site_id = siteFilter;
    if (subFilter) params.subcontractor_id = subFilter;
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const res = await api.get('/insaat/reports', { params });
      setReports(res.data);
    } catch (err) { console.error(err); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || '-';
  const subName = (id) => subs.find((s) => s.id === id)?.name || '-';
  const ustAdi = (id) => {
    const s = subs.find((x) => x.id === id);
    return s?.parent_id ? subs.find((x) => x.id === s.parent_id)?.name : null;
  };
  // Filtre sırası: her ana taşeronun hemen altında kendi alt taşeronları
  const filtreSirasi = subs.filter((s) => !s.parent_id)
    .flatMap((s) => [s, ...subs.filter((a) => a.parent_id === s.id)]);

  return (
    <div data-testid="insaat-report-list-page">
      <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight mb-6">Günlük Raporlar</h1>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Şantiye</label>
          <Select value={siteFilter} onValueChange={(v) => setSiteFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-44"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Alt Yüklenici</label>
          <Select value={subFilter} onValueChange={(v) => setSubFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-44"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              {filtreSirasi.map((s) => (
                <SelectItem key={s.id} value={s.id} className={s.parent_id ? 'pl-8' : ''}>
                  {s.parent_id ? `↳ ${s.name}` : s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Durum</label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-40"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              <SelectItem value="submitted">Gönderildi</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Başlangıç</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white border-[#d0c4b0]" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Bitiş</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white border-[#d0c4b0]" />
        </div>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Alt Yüklenici</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Şantiye</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutanak Toplamı</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id} className="erp-table-row border-[#d0c4b0] cursor-pointer hover:bg-[#f5f0e8]"
                onClick={() => navigate(`/erp/insaat/reports/${r.id}`)} data-testid={`report-row-${r.id}`}>
                <TableCell className="text-sm text-[#3F3E39]">{r.report_date}</TableCell>
                <TableCell className="font-medium text-[#1a1916]">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck size={14} className="text-[#8a7e70]" /> {subName(r.subcontractor_id)}
                  </div>
                  {ustAdi(r.subcontractor_id) && (
                    <p className="text-[11px] font-normal text-[#8a7e70] pl-6">{ustAdi(r.subcontractor_id)} alt taşeronu</p>
                  )}
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{siteName(r.site_id)}</TableCell>
                <TableCell className="text-right font-mono text-[#1a1916]">
                  {toplamMetni(r)}
                </TableCell>
                <TableCell>
                  <span className={`status-badge ${statusColors[r.status] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                    {statusLabels[r.status] || r.status}
                  </span>
                  {r.calisma_yok && (
                    <span className="status-badge bg-[#e8dfd0] text-[#5c554b] ml-1">Çalışma yok</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#8a7e70]">Rapor bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
